/**
 * 指标定义正本（10-27）：Plan 页面上每个指标的「回答的问题＋分子／分母」只写这一处，`MetricTip` 按编号取。
 * 编号＝页面组.字段名；口径照后端实际算法写（出处在每组注释），后端改算法要同步改这里。
 * den 为空＝不是比率，num 写「怎么算」。
 */

export type MetricDef = {
	name: string;
	/** 回答的问题 */
	q: string;
	/** 分子；不是比率时写怎么算 */
	num: string;
	/** 分母；不是比率时不写 */
	den?: string;
	/** 特殊口径（不计入的、空值等） */
	note?: string;
	better?: "high" | "low" | "none";
};

export const BETTER_ZH: Record<NonNullable<MetricDef["better"]>, string> = {
	high: "越高越好",
	low: "越低越好",
	none: "只看量，不分好坏",
};

/* 召回评测（单层回放）——后端 recall/metrics.py · score ／ knowledge_score，recall/replay.py · run（信号层） */
const RECALL = {
	"recall.tool_recall": {
		name: "工具召回率",
		q: "该用的工具有没有进候选前 N 名（N 见卡片小字，默认 6）",
		num: "前 N 名里含至少一个可接受工具的诉求数",
		den: "金标有可接受工具的诉求数",
		note: "按档位（推荐＞可选＞不推荐）再按排序取前 N；常备工具不计",
		better: "high",
	},
	"recall.tool_precision": {
		name: "推荐档精准率",
		q: "判成「推荐」的工具里有多少是对的",
		num: "推荐档里属于可接受工具或其前置工具的个数",
		den: "推荐档工具总个数（全部诉求合计）",
		note: "常备工具不计；一个都没推荐时为空",
		better: "high",
	},
	"recall.top1": {
		name: "首位精准率",
		q: "排在第一的是不是对的工具",
		num: "第 1 名是可接受工具的诉求数",
		den: "金标有可接受工具的诉求数",
		note: "按档位再按排序取第 1；常备工具不计",
		better: "high",
	},
	"recall.rec_hit": {
		name: "推荐档召回率",
		q: "对的工具有没有被判成「推荐」",
		num: "推荐档里含至少一个可接受工具的诉求数",
		den: "金标有可接受工具的诉求数",
		note: "排序服务调用失败的轮次全部按「可选」走，这一项会明显变低",
		better: "high",
	},
	"recall.avg_candidates": {
		name: "平均候选数",
		q: "每件诉求平均给了几个候选工具",
		num: "候选工具总数（不含常备）",
		den: "诉求总数",
		better: "none",
	},
	"recall.avg_recommended": {
		name: "平均推荐档数",
		q: "每件诉求平均有几个工具被判成「推荐」",
		num: "推荐档工具总数（不含常备）",
		den: "诉求总数",
		note: "太少容易漏、太多给决策添干扰",
		better: "none",
	},
	"recall.cannot_do_hit": {
		name: "无能办召回率",
		q: "金标「没有能办的工具」的诉求，有没有判成「无能办的」",
		num: "金标无能办的、且结论判成无能办的诉求数",
		den: "金标无能办的诉求数",
		note: "括号里的误报＝金标不是无能办的、却判成无能办的诉求件数",
		better: "high",
	},
	"recall.guide_recall": {
		name: "指南召回率",
		q: "该召回的应答指南召回了没有",
		num: "金标「应召回」且被召回的指南数",
		den: "金标「应召回」的指南数（全部决策点合计）",
		better: "high",
	},
	"recall.guide_false_recall": {
		name: "指南误召率",
		q: "不该召回的应答指南被召回了多少",
		num: "金标「应召回」以外、却被召回的指南数",
		den: "金标「应召回」以外的指南数（全部决策点合计）",
		better: "low",
	},
	"recall.knowledge_push_recall": {
		name: "知识召回率",
		q: "该推的知识推到了没有",
		num: "金标「该推」且推送了的知识条目数",
		den: "金标「该推」的知识条目数",
		note: "对知识推送金标；金标「拿不准」的不计",
		better: "high",
	},
	"recall.knowledge_false_push": {
		name: "知识误推率",
		q: "不该推的知识被推了多少",
		num: "金标「不该推」却推送了的知识条目数",
		den: "金标「不该推」的知识条目数",
		note: "金标「拿不准」的不计",
		better: "low",
	},
	"recall.knowledge_avg_pushed": {
		name: "平均推送条数",
		q: "每件诉求平均推了几条知识",
		num: "推送的知识条目总数",
		den: "有知识推送金标的诉求数",
		better: "none",
	},
	"recall.signal_count_match": {
		name: "诉求件数对上",
		q: "串联喂入时，信号节点抽出的诉求件数和金标是否一样",
		num: "抽出件数＝金标件数的决策点数",
		den: "决策点数",
		note: "只在串联喂入时有",
		better: "high",
	},
	"recall.signal_multi_hit": {
		name: "多诉求报全",
		q: "金标有两件及以上诉求时，信号节点是否也抽出了两件及以上",
		num: "金标 ≥2 件且抽出 ≥2 件的决策点数",
		den: "金标 ≥2 件的决策点数",
		note: "只在串联喂入时有；只看件数，不看内容是否对上",
		better: "high",
	},
} satisfies Record<string, MetricDef>;

/** 耗时分布四件套（最近秩法：排序后取第 round(q×(n−1)) 个，后端 eval/timing.py） */
function dist(kind: "p50" | "p90" | "max" | "mean", sample: string, extra?: string): MetricDef {
	const m = {
		p50: {
			name: "p50（中位数）",
			q: "一般要多久：一半样本比它快",
			num: `${sample}按耗时排序后的第 50 百分位`,
		},
		p90: {
			name: "p90",
			q: "慢的那一成要多久（看长尾）",
			num: `${sample}按耗时排序后的第 90 百分位`,
		},
		max: { name: "最长", q: "最慢的一次要多久", num: `${sample}里耗时最长的一个` },
		mean: {
			name: "平均",
			q: "平均要多久（会被少数特别慢的拉高）",
			num: `${sample}耗时之和 ÷ 个数`,
		},
	}[kind];
	return { ...m, note: extra, better: "low" };
}

/* 统计——后端 eval/timing.py（batch_row 151-203、题族 216-238、分段 327-438、单轮 452-505）、store/trace_view.py */
const STATS = {
	"stats.wall_s": {
		name: "墙钟",
		q: "这一批从开跑到跑完，用户实际等了多久",
		num: "本批全部尝试（原跑＋补跑）里最早的开始到最晚的结束",
		note: "有补跑时含中间的间隔；卡片取最近一个有墙钟的批次",
		better: "low",
	},
	"stats.trial_p50_s": dist("p50", "本批每个试跑（只取最新一次尝试）", "不含被补跑替换掉的旧尝试"),
	"stats.trial_p90_s": dist("p90", "本批每个试跑（只取最新一次尝试）", "不含被补跑替换掉的旧尝试"),
	"stats.trial_max_s": dist("max", "本批每个试跑（只取最新一次尝试）"),
	"stats.trial_p": {
		name: "单次 p50 ／ p90 ／ 最长",
		q: "单次试跑一般多久、长尾多长、最慢多久",
		num: "本批每个试跑只取最新一次尝试的耗时，分别取第 50、90 百分位和最大值",
		note: "一次都没跑显示「没跑」",
		better: "low",
	},
	"stats.trial_sum_s": {
		name: "试跑耗时之和",
		q: "机器一共花了多少时间跑试跑",
		num: "全部尝试（含补跑）的耗时相加",
		note: "和 p50 ／ p90 的样本不同：这里含补跑",
		better: "low",
	},
	"stats.parallelism": {
		name: "等效并发",
		q: "实际跑出了几路并行",
		num: "试跑耗时之和",
		den: "墙钟",
		note: "越接近所设并发越好；墙钟为空或 0 时显示「—」",
		better: "high",
	},
	"stats.queue_wait_s": {
		name: "排队等待",
		q: "前面的批次挡了这批多久",
		num: "开跑时间 − 建批时间（还在排队的用当前时间算）",
		note: "≥ 60 秒标黄",
		better: "low",
	},
	"stats.cost_usd": {
		name: "花费",
		q: "这一批花了多少钱",
		num: "账本里本批全部模型调用的费用相加；没回实际费用的按 token 估算",
		note: "含估算，和「耗时分段」里的「实际花费」（只加实际费用）口径不同；单位美元，DeepSeek 官网按人民币计价、按配置汇率折算（汇率见配置版本页「运行环境」）",
		better: "low",
	},
	"stats.undetermined": {
		name: "未判定",
		q: "有几次试跑没出结论",
		num: "最新一次尝试结论为未判定的试跑数（悬停数字看原因，限流单列）",
		note: "和批次报告同一口径",
		better: "low",
	},
	"stats.family_p50_s": dist("p50", "该题族每个试跑（只取最新一次尝试）"),
	"stats.family_p90_s": dist("p90", "该题族每个试跑（只取最新一次尝试）"),
	"stats.family_max_s": dist("max", "该题族每个试跑（只取最新一次尝试）"),
	"stats.family_mean_s": dist("mean", "该题族每个试跑（只取最新一次尝试）"),
	"stats.family_share": {
		name: "占试跑耗时（题族）",
		q: "这类题吃掉了多少时间",
		num: "该题族耗时之和",
		den: "各题族耗时之和",
		note: "只算最新一次尝试，和整批「试跑耗时之和」（含补跑）不同",
		better: "none",
	},
	"stats.seg_seconds": {
		name: "合计耗时（环节）",
		q: "时间花在了哪个环节",
		num: "被测 agent 整轮＝运行记录每轮耗时之和与各节点模型耗时之和取大；子节点、模拟客户、判官、CX＝账本该段耗时相加；非模型部分＝整轮 − agent 模型耗时（不小于 0）",
		better: "low",
	},
	"stats.seg_share": {
		name: "占试跑耗时（环节）",
		q: "各环节占了多少时间",
		num: "该环节耗时",
		den: "试跑耗时之和（含补跑）",
		note: "不是除以墙钟",
		better: "none",
	},
	"stats.seg_calls": {
		name: "模型调用",
		q: "这个环节调了几次模型",
		num: "账本里该环节的调用条数",
		note: "没有模型调用的行显示「—」",
		better: "none",
	},
	"stats.seg_call_p": {
		name: "单次调用 p50 ／ p90",
		q: "这个环节单次模型调用一般多久、长尾多长",
		num: "该环节每次模型调用的耗时，取第 50、90 百分位",
		better: "low",
	},
	"stats.seg_cost": {
		name: "实际花费（环节）",
		q: "这个环节花了多少钱",
		num: "该环节有实际费用的调用费用相加",
		note: "不含估算，和批次列表的「花费」（含估算）口径不同；单位美元，DeepSeek 按人民币计价、按配置汇率折算",
		better: "low",
	},
	"stats.agent_share": {
		name: "被测 agent 占比",
		q: "被测客服 agent 本身占了多少时间",
		num: "被测 agent 整轮耗时",
		den: "试跑耗时之和（含补跑）",
		better: "none",
	},
	"stats.unrecorded_share": {
		name: "未记账占比",
		q: "调度、写盘、重试等待这类没记进账本的时间有多少",
		num: "试跑耗时之和 − agent 整轮 − 模拟客户 − 判官 − CX（不小于 0）",
		den: "试跑耗时之和（含补跑）",
		note: "> 10% 标黄；10-20 之前的批次 CX 没进账本，会落在这里",
		better: "low",
	},
	"stats.total_wall_s": {
		name: "整批墙钟（范围内）",
		q: "所选范围内各批用户实际等了多久",
		num: "所选批次的墙钟相加",
		note: "选多批时是各批墙钟相加，不是跨批的总时长",
		better: "low",
	},
	"stats.turn_n": {
		name: "轮数",
		q: "统计了多少轮对话",
		num: "所选天数内运行记录的轮数（按记录号去重）",
		note: "来源筛选不作用于这张表，固定出全部／人工提问／评测三行",
		better: "none",
	},
	"stats.turn_p50": dist("p50", "每轮端到端耗时"),
	"stats.turn_p90": dist("p90", "每轮端到端耗时"),
	"stats.turn_max": dist("max", "每轮端到端耗时"),
	"stats.turn_mean": dist("mean", "每轮端到端耗时"),
	"stats.node_n": {
		name: "出现轮数",
		q: "这个节点在多少轮里被调用过",
		num: "账本里出现过该节点的轮数",
		better: "none",
	},
	"stats.node_calls_per_turn": {
		name: "每轮调用",
		q: "出现这个节点的轮里，平均调了几次",
		num: "该节点调用总次数",
		den: "出现轮数",
		note: "非模型部分和整轮两行显示「—」",
		better: "none",
	},
	"stats.node_p50": dist("p50", "出现该节点的每一轮（该轮该节点调用耗时相加）"),
	"stats.node_p90": dist("p90", "出现该节点的每一轮（该轮该节点调用耗时相加）"),
	"stats.node_max": dist("max", "出现该节点的每一轮（该轮该节点调用耗时相加）"),
	"stats.node_mean": dist("mean", "出现该节点的每一轮（该轮该节点调用耗时相加）"),
} satisfies Record<string, MetricDef>;

/* 评测批次报告 ／ 评测记录——后端 eval/report.py · build、eval/metrics.py · aggregate、eval/judges.py、eval/emotion_risk.py、eval/jobs.py */
const VALID = "有效次数（结论为通过或失败的试跑数；未判定、运行中的不算）";
const EVAL = {
	"eval.pass_rate": {
		name: "通过率",
		q: "严格口径下，办对的试跑占多少",
		num: "结论为通过的试跑数",
		den: VALID,
		note: "通过＝各计分维度都过，且写对象错误、不该做的提交、重复提交都为 0（安全否决）；小字里「不扣」是不看安全否决的对照",
		better: "high",
	},
	"eval.avg_reward": {
		name: "通过率",
		q: "这一批严格口径下办对的试跑占多少",
		num: "结论为通过的试跑数",
		den: VALID,
		note: "和批次报告的「通过率」同一口径（扣安全否决）；筛选框按 0～1 填（0.9＝90%）",
		better: "high",
	},
	"eval.action": {
		name: "action 通过率",
		q: "该调的工具调对了没有",
		num: "动作维通过的试跑数",
		den: "有这一维的有效次数",
		note: "动作维＝工具调用对，且确定性检查里动作类没有失败；不扣安全否决。小字的机判 ／ 没过 ／ 未机判按检查条数计",
		better: "high",
	},
	"eval.communicate": {
		name: "communicate 通过率",
		q: "该告诉客户的信息说到了没有",
		num: "必说信息都说到了的试跑数",
		den: "设了必说信息的有效次数",
		note: "必说信息在机器人回复里出现才算说到，且确定性检查里沟通类没有失败；没设必说信息的试跑不进分母",
		better: "high",
	},
	"eval.nl": {
		name: "nl 合规率",
		q: "话术合规断言都满足了没有",
		num: "已判断言全部满足的试跑数",
		den: "合规维有结论的有效次数",
		note: "断言全部不适用的试跑不进分母；小字「不适用 N 条」按断言条数计",
		better: "high",
	},
	"eval.n": {
		name: "有效样本",
		q: "能算通过率的样本有多少",
		num: "结论为通过 ＋ 结论为失败的试跑数",
		note: "小字「共 x 题」是本批出现过的题数",
		better: "none",
	},
	"eval.undetermined": {
		name: "未判定",
		q: "有几次试跑没出结论",
		num: "结论为未判定的试跑数（按原因分组）",
		note: "不进通过率、不进 pass^k",
		better: "low",
	},
	"eval.write_object_errors": {
		name: "写对象错误",
		q: "有没有把单或行项写错",
		num: "各试跑写错对象的次数相加（订单或行项对不上金标）",
		note: "是次数不是题数；安全否决项，有一次该试跑就不算通过",
		better: "low",
	},
	"eval.forbidden_commits": {
		name: "不该做的提交",
		q: "有没有做了金标说不该做的写操作",
		num: "各试跑不该做的提交次数相加",
		note: "安全否决项",
		better: "low",
	},
	"eval.duplicate_commits": {
		name: "重复提交",
		q: "有没有同一件事提交了两次",
		num: "各试跑同工具同对象提交两次及以上的次数相加",
		note: "安全否决项",
		better: "low",
	},
	"eval.false_claims": {
		name: "没办成说办成",
		q: "嘴上说办了、系统里其实没办的有多少",
		num: "每轮声明核对不一致的条数，先在试跑内相加，再全批相加",
		note: "观测项，不进安全否决",
		better: "low",
	},
	"eval.guidance": {
		name: "指南遵守（观测）",
		q: "有没有照应答指南做",
		num: "遵守的指南断言条数",
		den: "已判的指南断言条数（只算有效试跑）",
		note: "没判过显示「未判分」；有违反的试跑时标黄；不进通过率",
		better: "high",
	},
	"eval.fabrication": {
		name: "编造事实（观测）",
		q: "有没有编造事实",
		num: "有编造的试跑数",
		den: "判过编造的试跑数",
		note: "不进通过率",
		better: "low",
	},
	"eval.pass_k": {
		name: "pass^k（跨题平均）",
		q: "同一道题连跑 k 次是不是每次都对",
		num: "每题：从有效次数里随机抽 k 次全通过的概率 C(通过数, k) ÷ C(有效数, k)，再对题取平均",
		note: "k 从 1 到各题里最小的有效次数；显示 0～1 小数；次数不足的题不算（和按族同口径）",
		better: "high",
	},
	"eval.stable_pass": {
		name: "稳定过",
		q: "有几道题每次都过",
		num: "有效次数达到规定次数、且全部通过的题数",
		note: "规定次数：多诉求题 5 次、其余 3 次",
		better: "high",
	},
	"eval.flaky": {
		name: "摇摆",
		q: "有几道题时过时不过",
		num: "有效次数达到规定次数、有过有败的题数",
		better: "low",
	},
	"eval.stable_fail": {
		name: "稳定败",
		q: "有几道题每次都不过",
		num: "有效次数达到规定次数、且全部失败的题数",
		better: "low",
	},
	"eval.insufficient": {
		name: "次数不足",
		q: "有几道题有效次数不够、没法判稳定性",
		num: "有效次数少于规定次数的题数",
		note: "不进稳定分类，也不进按族的 pass^1 ／ pass^3",
		better: "low",
	},
	"eval.family_pass_1": {
		name: "pass^1（按族）",
		q: "这个题族的通过率",
		num: "去掉次数不足的题后，各题通过数 ÷ 有效数的平均",
		note: "先按题算再平均，不是按试跑汇总；没有可算的题显示「—」",
		better: "high",
	},
	"eval.family_pass_3": {
		name: "pass^3（按族）",
		q: "这个题族连跑 3 次都对的概率",
		num: "去掉次数不足的题、只取有效次数 ≥ 3 的题，各题 C(通过数, 3) ÷ C(有效数, 3) 的平均",
		better: "high",
	},
	"eval.item_completion": {
		name: "事项办成率",
		q: "这道题的诉求办成了几成",
		num: "每次有效试跑算「办成的已激活事项 ÷ 已激活事项」，再对试跑取平均",
		note: "金标事项没建出来也算已激活、记没办成；没有已激活事项的试跑跳过",
		better: "high",
	},
	"eval.er_sentiment": {
		name: "情绪准确率",
		q: "信号判客户情绪准不准",
		num: "命中轮数（实际情绪落在期望或可接受里；信号给空算没命中）",
		den: "判过的轮数",
		note: "只算有效试跑里金标带情绪风险期望的题；没对上金标的轮不进分母；观测项，不进通过率",
		better: "high",
	},
	"eval.er_session_missed": {
		name: "整段漏报",
		q: "整段对话里该报的风险旗有没有漏",
		num: "整段应出现、但一次都没报过的旗次数",
		den: "整段应出现的旗次数",
		better: "low",
	},
	"eval.er_session_false": {
		name: "整段误报",
		q: "整段对话里不该报的风险旗有没有报",
		num: "整段不该出现、却报过的旗次数",
		den: "整段不该出现的旗次数",
		better: "low",
	},
	"eval.er_negative": {
		name: "反例题误报",
		q: "本来没有风险的题有没有乱报风险旗",
		num: "有误报的试跑数（括号里是误报的旗个数）",
		den: "反例题的试跑数（反例题＝整段不该出现任何风险旗）",
		better: "low",
	},
	"eval.er_flag_recall": {
		name: "召回率（风险旗）",
		q: "该报的这个旗报出来没有",
		num: "命中（逐轮逐旗累计）",
		den: "应报",
		note: "落在「可接受」里的不计；应报为 0 显示「—」。和召回评测的「工具召回率」不是一回事",
		better: "high",
	},
	"eval.er_flag_false_rate": {
		name: "误报率（风险旗）",
		q: "不该报的这个旗被报了多少",
		num: "误报（逐轮逐旗累计）",
		den: "不该报",
		note: "不该报为 0 显示「—」",
		better: "low",
	},
} satisfies Record<string, MetricDef>;

export const METRIC_DEFS = {
	...RECALL,
	...STATS,
	...EVAL,
} satisfies Record<string, MetricDef>;

export type MetricId = keyof typeof METRIC_DEFS;
